/**
 * One narrow allowance for a Supabase platform fault (D27).
 *
 * PostgREST checks a token's `iat` against its own, cached clock with no
 * leeway. When that clock reads behind the auth server's, a token used in the
 * second it was minted is refused: 401, code `PGRST303`, "JWT issued at
 * future". It is a known, open Supabase issue on hosted projects (supabase
 * discussion #48123); PostgREST's own allowance for skew is not released. The
 * product meets it at exactly two moments: the first read after signing in,
 * and the first after each hourly token refresh.
 *
 * The refusal happens while the token is validated, **before any statement
 * runs**, so repeating that one request cannot do anything twice. This
 * repeats it once: after the token's issued-at second has passed (bounded to
 * two seconds), and only for a Data API request refused with exactly that
 * code. Every other answer — any other 401, a second `PGRST303`, anything that
 * is not the Data API — is handed back untouched. Not a retry policy: a wait
 * for the one thing known to fix itself.
 */
export const JWT_ISSUED_AT_FUTURE = 'PGRST303';
export const ISSUED_AT_GRACE_MAX_MS = 2_000;

interface GraceOptions {
    sleep?: (ms: number) => Promise<void>;
    now?: () => number;
}

const defaultSleep = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms));

/** The `iat` of a bearer token, read without verifying it: only to know how long to wait. */
export function tokenIssuedAt(authorization: string | null): number | null {
    const token = /^Bearer\s+([^.\s]+)\.([^.\s]+)\.[^.\s]*$/i.exec(authorization ?? '')?.[2];
    if (!token) return null;
    try {
        const json = atob(token.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(token.length / 4) * 4, '='));
        const iat = (JSON.parse(json) as { iat?: unknown }).iat;
        return typeof iat === 'number' && Number.isFinite(iat) ? iat : null;
    } catch {
        return null;
    }
}

const urlOf = (input: RequestInfo | URL): string =>
    typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;

const authorizationOf = (input: RequestInfo | URL, init?: RequestInit): string | null =>
    new Headers(init?.headers ?? (typeof input === 'object' && 'headers' in input ? input.headers : undefined)).get('authorization');

/** Wraps a fetch so a Data API request refused as "issued at future" is made once more, after the wait. */
export function withIssuedAtGrace(fetchImpl: typeof fetch, options: GraceOptions = {}): typeof fetch {
    const sleep = options.sleep ?? defaultSleep;
    const now = options.now ?? Date.now;
    return async (input, init) => {
        const response = await fetchImpl(input, init);
        if (response.status !== 401 || !urlOf(input).includes('/rest/v1/')) return response;
        let code: unknown;
        try {
            code = ((await response.clone().json()) as { code?: unknown }).code;
        } catch {
            return response;
        }
        if (code !== JWT_ISSUED_AT_FUTURE) return response;
        // Only a request that can be sent again as it was: no body, or a body that is text.
        const streamed = (typeof input === 'object' && !(input instanceof URL) && input.body !== null)
            || (init?.body !== undefined && init.body !== null && typeof init.body !== 'string');
        if (streamed) return response;
        const iat = tokenIssuedAt(authorizationOf(input, init));
        const wait = iat === null ? 1_000 : (iat + 1) * 1_000 - now() + 250;
        await sleep(Math.min(ISSUED_AT_GRACE_MAX_MS, Math.max(0, wait)));
        return fetchImpl(input, init);
    };
}
